<template>
  <div class="itinerary">
    <div v-if="!hideDetails" class="title">
      <span class="statprice">
        <strong>{{ itinerary.status }}</strong>
        <span>&nbsp;&middot;&nbsp;</span>
        <span>{{ total }}</span>
      </span>
      <span v-if="!hideDetails" class="links">
        <span class="icon">&#x1f5a8;</span>
        <a @click="print">Itinerary</a>
        <span v-if="(itinerary.isBooked || itinerary.isCanceled) && !isHidePrices">&nbsp;&middot;&nbsp;</span>
        <a v-if="(itinerary.isBooked || itinerary.isCanceled) && !isHidePrices" @click="receipt">Receipt</a>
      </span>
    </div>
    <Confirmation v-if="itinerary.isBooked && !itinerary.isCanceled" :confirmations="confirmations" />
    <div class="items">
      <div v-for="(s, i) in itinerary.schedule" :key="i">
        <Flight
          v-if="s.itemType == 'flight' && appendixFlights[s.itemId]"
          :airline-iata="appendixFlights[s.itemId].airline_iata"
          :airline-iata-op="appendixFlights[s.itemId].airline_iata_op"
          :airline-title="appendixFlights[s.itemId].airline_title"
          :airline-title-op="appendixFlights[s.itemId].airline_title_op"
          :is-codeshare="appendixFlights[s.itemId].is_codeshare"
          :airport-depart-iata="appendixFlights[s.itemId].airport_depart_iata"
          :airport-depart-title="appendixFlights[s.itemId].airport_depart_title"
          :airport-arrive-iata="appendixFlights[s.itemId].airport_arrive_iata"
          :airport-arrive-title="appendixFlights[s.itemId].airport_arrive_title"
          :cabin="appendixFlights[s.itemId].cabin"
          :elapsed="appendixFlights[s.itemId].elapsed"
          :number="appendixFlights[s.itemId].number"
          :status="appendixFlights[s.itemId].status"
          :time-depart="appendixFlights[s.itemId].time_depart"
          :time-depart-original="appendixFlights[s.itemId].time_depart_original"
          :time-arrive="appendixFlights[s.itemId].time_arrive"
          :time-arrive-original="appendixFlights[s.itemId].time_arrive_original"
        />
        <Accommodation v-if="s.itemType == 'accommodation' && appendixAccommodations[s.itemId]" :accommodation="appendixAccommodations[s.itemId]" />
        <Transport v-if="s.itemType == 'transport' && appendixTransports[s.itemId]" :transport="appendixTransports[s.itemId]" />
        <div v-if="s.itemType == 'limited'">This view is limited to 1 Year Maximum. Please visit Reports to find older bookings</div>
        <ItinerarySeparator v-if="s.itemType == 'separator' && i < itinerary.schedule.length - 1" />
        <ItinerarySpacer v-else-if="i < itinerary.schedule.length - 1 && itinerary.schedule[i + 1].itemType != 'separator' && (i === 0 || (i > 0 && itinerary.schedule[i - 1].itemType != 'limited'))" :layover="layover(i)" />
      </div>
    </div>
    <Bookings v-if="confirmations && itinerary.isBooked && !hideDetails" :bookings="itinerary.bookings" :confirmations="confirmations" :is-hide-prices="isHidePrices" />
    <Quotes v-if="!itinerary.isBooked" :quotes="itinerary.quotes" :is-hide-prices="isHidePrices" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";

import Accommodation from "./ItineraryAccommodation.vue";
import Confirmation from "./ItineraryConfirmation.vue";
import Bookings from "./ItineraryBookings.vue";
import Quotes from "./ItineraryQuotes.vue";
import Flight from "./ItineraryFlight.vue";
import ItinerarySpacer from "./ItinerarySpacer.vue";
import ItinerarySeparator from "./ItinerarySeparator.vue";
import Transport from "./ItineraryTransport.vue";
import useApi from "../../utils/apiCalls";
import DateTime from "../../utils/datetime";

const { utilDurationItineraryMinutes } = DateTime();

// Props
const props = defineProps({
  appendixAccommodations: Object,
  appendixFlights: Object,
  appendixTransports: Object,
  itinerary: Object,
  isHidePrices: Boolean,
  hideDetails: Boolean,
  tripId: String,
  isVisible: Boolean,
});

// Emits
const emit = defineEmits(["printItinerary", "printReceipt", "error"]);
const { apiGetRequest } = useApi(emit);
const confirmations = ref(null);

const loadConfimations = () => {
  if (props.itinerary.isBooked /* && !props.itinerary.isCanceled*/) {
    apiGetRequest({
      path: `/trips/${props.tripId}/confirmation-info/${props.itinerary.id}`,
      onSuccess: data => {
        confirmations.value = data;
      },
    });
  }
};

watch(
  () => props.isVisible,
  isVisible => {
    if (isVisible) loadConfimations();
  }
);

onMounted(() => {
  if (props.isVisible) loadConfimations();
});

// Computed properties
const total = computed(() => {
  if (props.isHidePrices) return "NA";
  const currency = props.itinerary.totalCostCurrency;
  const cost = props.itinerary.totalCost.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currency} ${cost}`;
});

// Methods
const layover = index => {
  const schedule = props.itinerary.schedule;
  if (index < schedule.length - 1 && schedule[index].itemType === "flight" && schedule[index + 1].itemType === "flight") {
    if (!props.appendixFlights[schedule[index].itemId] || !props.appendixFlights[schedule[index + 1].itemId]) return false;

    const flight1Arrive = new Date(props.appendixFlights[schedule[index].itemId].time_arrive);
    const flight2Depart = new Date(props.appendixFlights[schedule[index + 1].itemId].time_depart);
    var minutes = (flight2Depart - flight1Arrive) / 1000 / 60;
    return utilDurationItineraryMinutes(minutes);
  }
  return "";
};

const print = () => {
  emit("printItinerary");
};

const receipt = () => {
  emit("printReceipt");
};
</script>
